import { router } from '@inertiajs/react'
import { useEffect } from 'react'
import { cable } from './cable'

// Live conversations inbox + badge (architecture §6.18). Subscribes to the
// ConversationListChannel and, on any "your inbox changed" nudge, re-fetches the
// nav badge (`conversations_unread`, a shared prop on every page) and — when the
// inbox is on screen — the conversation list. This keeps unread counts correct
// live across EVERY conversation, not just the open thread, with no polling.
//
// Reloads are debounced so a burst of messages coalesces into one refresh.
// Operators subscribe with no params (they see all); a portal user passes their
// active company id and is only streamed that company's customer-visible activity.
export function useConversationsLive(
  opts: { customerId?: string | null; enabled?: boolean } = {},
): void {
  const { customerId, enabled = true } = opts

  useEffect(() => {
    if (!enabled) return

    let timer: ReturnType<typeof setTimeout> | null = null
    function refresh() {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        router.reload({ only: ['conversations_unread', 'conversations'] })
      }, 250)
    }

    const params = customerId ? { customer_id: customerId } : {}
    const subscription = cable().subscriptions.create(
      { channel: 'ConversationListChannel', ...params },
      {
        received(data: { type?: string }) {
          if (data?.type === 'conversations_changed') refresh()
        },
      },
    )

    return () => {
      if (timer) clearTimeout(timer)
      subscription.unsubscribe()
    }
  }, [customerId, enabled])
}
